import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Replace {{key}} with field values
export function interpolate(text, values) {
  if (!text) return '';
  return text.replace(/\{\{(\w+)\}\}/g, (_, k) => {
    const v = values[k];
    if (v === undefined || v === null || v === '') return '_________';
    return String(v);
  });
}

const PAGE_W = 595.28; // A4
const PAGE_H = 841.89;
const MARGIN = 64;

function newDoc() {
  return new jsPDF({ unit: 'pt', format: 'a4' });
}

function writeWrapped(doc, text, x, y, maxWidth, lineHeight = 16) {
  const lines = doc.splitTextToSize(String(text ?? ''), maxWidth);
  for (const ln of lines) {
    if (y > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
    doc.text(ln, x, y);
    y += lineHeight;
  }
  return y;
}

// ───────── Document renderer ─────────
function renderDocument(template, values) {
  const doc = newDoc();
  let y = MARGIN;
  const maxW = PAGE_W - MARGIN * 2;
  const centered = !!template.centered;

  for (const block of template.body) {
    const txt = interpolate(block.text, values);

    if (block.type === 'h1') {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(20, 20, 20);
      if (centered) {
        doc.text(txt, PAGE_W / 2, y, { align: 'center' });
        y += 30;
      } else {
        y = writeWrapped(doc, txt, MARGIN, y, maxW, 24);
        y += 12;
      }
    } else if (block.type === 'h2') {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(40, 40, 40);
      if (centered) {
        doc.text(txt, PAGE_W / 2, y, { align: 'center' });
        y += 22;
      } else {
        y = writeWrapped(doc, txt, MARGIN, y, maxW, 18);
        y += 4;
      }
    } else if (block.type === 'p') {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(40, 40, 40);
      if (centered) {
        const lines = doc.splitTextToSize(txt, maxW);
        for (const ln of lines) {
          if (y > PAGE_H - MARGIN) { doc.addPage(); y = MARGIN; }
          doc.text(ln, PAGE_W / 2, y, { align: 'center' });
          y += 16;
        }
        y += 6;
      } else {
        y = writeWrapped(doc, txt, MARGIN, y, maxW, 16);
        y += 6;
      }
    } else if (block.type === 'spacer') {
      y += 18;
    } else if (block.type === 'sign') {
      y += 30;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const cols = block.labels.length;
      const colW = maxW / cols;
      for (let i = 0; i < cols; i++) {
        const x = MARGIN + colW * i;
        doc.setDrawColor(160);
        doc.line(x, y, x + colW - 24, y);
        doc.text(interpolate(block.labels[i], values), x, y + 14);
      }
      y += 36;
    }
  }
  return doc;
}

// ───────── Invoice/Receipt/PO renderer ─────────
function renderInvoice(template, values) {
  const doc = newDoc();
  const maxW = PAGE_W - MARGIN * 2;
  const isReceipt = template.receipt;
  const isPO = template.po;
  const isQuote = template.quote;

  // Header label
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(226, 91, 69);
  const label = isReceipt ? 'RECEIPT' : isPO ? 'PURCHASE ORDER' : isQuote ? 'QUOTE' : 'INVOICE';
  doc.text(label, MARGIN, MARGIN + 8);

  // Business info (top-left under label) and invoice meta (top-right)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);

  let y = MARGIN + 36;
  doc.setFont('helvetica', 'bold');
  doc.text(values.businessName || '', MARGIN, y);
  doc.setFont('helvetica', 'normal');
  y = writeWrapped(doc, values.businessAddress || '', MARGIN, y + 14, 240, 13);
  if (values.businessEmail) {
    doc.text(values.businessEmail, MARGIN, y);
    y += 14;
  }

  // Meta (right side)
  const rightX = PAGE_W - MARGIN;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  let metaY = MARGIN + 36;
  const numLabel = isReceipt ? 'Receipt #' : isPO ? 'PO #' : isQuote ? 'Quote #' : 'Invoice #';
  doc.text(numLabel, rightX, metaY, { align: 'right' });
  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.text(values.invoiceNumber || '', rightX, metaY + 14, { align: 'right' });

  metaY += 36;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text('Date', rightX, metaY, { align: 'right' });
  doc.setTextColor(20, 20, 20);
  doc.text(values.invoiceDate || '', rightX, metaY + 14, { align: 'right' });

  if (values.dueDate) {
    metaY += 36;
    doc.setTextColor(120, 120, 120);
    doc.text(isPO ? 'Delivery Date' : isQuote ? 'Valid Until' : 'Due Date', rightX, metaY, { align: 'right' });
    doc.setTextColor(20, 20, 20);
    doc.text(values.dueDate, rightX, metaY + 14, { align: 'right' });
  }

  // Bill To
  y = Math.max(y, metaY + 36) + 16;
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  const billLabel = isReceipt ? 'RECEIVED FROM' : isPO ? 'VENDOR' : isQuote ? 'QUOTE FOR' : 'BILL TO';
  doc.text(billLabel, MARGIN, y);
  y += 14;
  doc.setFontSize(11);
  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.text(values.clientName || '', MARGIN, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  y = writeWrapped(doc, values.clientAddress || '', MARGIN, y + 14, 240, 13);

  // Items table
  const items = (values.items || []).map(it => ({
    description: it.description || '',
    quantity: Number(it.quantity) || 0,
    rate: Number(it.rate) || 0,
    amount: (Number(it.quantity) || 0) * (Number(it.rate) || 0),
  }));

  autoTable(doc, {
    startY: y + 18,
    head: [['Description', 'Qty', 'Rate', 'Amount']],
    body: items.map(it => [it.description, String(it.quantity), `$${it.rate.toFixed(2)}`, `$${it.amount.toFixed(2)}`]),
    headStyles: { fillColor: [247, 245, 242], textColor: [60, 60, 60], fontStyle: 'bold' },
    bodyStyles: { textColor: [40, 40, 40] },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    margin: { left: MARGIN, right: MARGIN },
    styles: { fontSize: 10, cellPadding: 8 },
  });

  // Totals
  const subtotal = items.reduce((s, it) => s + it.amount, 0);
  const taxRate = Number(values.taxRate) || 0;
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;
  const afterTableY = doc.lastAutoTable.finalY + 18;

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text('Subtotal', rightX - 110, afterTableY);
  doc.text(`$${subtotal.toFixed(2)}`, rightX, afterTableY, { align: 'right' });
  if (taxRate > 0) {
    doc.text(`Tax (${taxRate}%)`, rightX - 110, afterTableY + 16);
    doc.text(`$${tax.toFixed(2)}`, rightX, afterTableY + 16, { align: 'right' });
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text('Total', rightX - 110, afterTableY + (taxRate > 0 ? 38 : 22));
  doc.setTextColor(226, 91, 69);
  doc.text(`$${total.toFixed(2)}`, rightX, afterTableY + (taxRate > 0 ? 38 : 22), { align: 'right' });

  // Notes
  if (values.notes) {
    const notesY = afterTableY + 70;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text('Notes', MARGIN, notesY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    writeWrapped(doc, values.notes, MARGIN, notesY + 14, maxW, 14);
  }

  return doc;
}

// ───────── Resume renderer ─────────
function renderResume(template, values) {
  const doc = newDoc();
  const maxW = PAGE_W - MARGIN * 2;

  // Name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(20, 20, 20);
  doc.text(values.fullName || '', MARGIN, MARGIN + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(226, 91, 69);
  doc.text(values.title || '', MARGIN, MARGIN + 32);

  // Contact line
  const contact = [values.email, values.phone, values.location, values.website].filter(Boolean).join('  •  ');
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(contact, MARGIN, MARGIN + 50);

  doc.setDrawColor(220);
  doc.line(MARGIN, MARGIN + 62, PAGE_W - MARGIN, MARGIN + 62);

  let y = MARGIN + 84;

  const section = (label, content) => {
    if (!content) return;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(226, 91, 69);
    doc.text(label.toUpperCase(), MARGIN, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    y = writeWrapped(doc, content, MARGIN, y, maxW, 14);
    y += 14;
  };

  section('Summary', values.summary);
  section('Experience', values.experience);
  section('Education', values.education);

  if (values.skills) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(226, 91, 69);
    doc.text('SKILLS', MARGIN, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    const skills = String(values.skills).split(',').map(s => s.trim()).filter(Boolean);
    // Render skills as chips
    let x = MARGIN;
    for (const s of skills) {
      const w = doc.getTextWidth(s) + 14;
      if (x + w > PAGE_W - MARGIN) { x = MARGIN; y += 20; }
      doc.setDrawColor(220);
      doc.roundedRect(x, y - 10, w, 16, 8, 8);
      doc.text(s, x + 7, y + 1);
      x += w + 6;
    }
  }

  return doc;
}

function applyWatermark(doc) {
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    // Diagonal big watermark
    doc.setTextColor(226, 91, 69);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(72);
    // jsPDF GState for opacity
    try {
      const gs = new doc.GState({ opacity: 0.08 });
      doc.setGState(gs);
    } catch (e) {}
    doc.text('EVERYTHING PDF', PAGE_W / 2, PAGE_H / 2, { align: 'center', angle: 35 });
    try {
      const gs = new doc.GState({ opacity: 1 });
      doc.setGState(gs);
    } catch (e) {}
    // Bottom footer line
    doc.setTextColor(140, 140, 140);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      'Made with Everything PDF — upgrade to Pro to remove this watermark.',
      PAGE_W / 2,
      PAGE_H - 24,
      { align: 'center' }
    );
  }
}

export function generatePdf(template, values, opts = {}) {
  let doc;
  if (template.type === 'invoice') doc = renderInvoice(template, values);
  else if (template.type === 'resume') doc = renderResume(template, values);
  else doc = renderDocument(template, values);
  if (opts.watermark) applyWatermark(doc);
  return doc;
}

export function downloadPdf(template, values, filename, opts = {}) {
  const doc = generatePdf(template, values, opts);
  const name = filename || `${template.id}-${Date.now()}.pdf`;
  doc.save(name);
}

export function getPdfDataUrl(template, values) {
  const doc = generatePdf(template, values);
  return doc.output('datauristring');
}

export function getPdfBlobUrl(template, values) {
  const doc = generatePdf(template, values);
  return doc.output('bloburl');
}
